import type { Component } from 'vue'
import { RefreshIcon, ShieldIcon } from '@solar-icons/vue/linear'

import {
  type SetupRunErrorMessages,
  type SetupRunStageLines,
  runFailureMessageKey,
} from '@/components/setup/setup-run'
import type {
  CompanySizeBand,
  CustomerProfileErrorCode,
  CustomerProfileRefusalReason,
  CustomerProfileRun,
  CustomerProfileRunStage,
  ProfileRowId,
} from '@/types/customer-profile'
import { isRunFailed } from '@/types/setup-run'

/** The line under a failed draft for each error code the run can end with. */
export const CUSTOMER_PROFILE_ERROR_MESSAGES = {
  customer_profile_enqueue_failed: 'setup.customerProfile.reading.errors.enqueueFailed',
  customer_profile_failed: 'setup.customerProfile.reading.errors.draftFailed',
  customer_profile_facts_missing: 'setup.customerProfile.reading.errors.factsMissing',
  run_timed_out: 'setup.customerProfile.reading.errors.timedOut',
} as const satisfies Record<CustomerProfileErrorCode, string> & SetupRunErrorMessages

/** The line under a draft that could not start. */
export const CUSTOMER_PROFILE_START_FAILED_KEY = 'setup.customerProfile.reading.errors.startFailed'

export function customerProfileFailureMessageKey(
  run: CustomerProfileRun | null,
  startFailed: boolean,
): string {
  if (startFailed || run === null || !isRunFailed(run)) return CUSTOMER_PROFILE_START_FAILED_KEY
  return runFailureMessageKey(run, CUSTOMER_PROFILE_ERROR_MESSAGES)
}

/** The message shown for each reason the server refuses a customer-profile call with. */
export const CUSTOMER_PROFILE_REFUSAL_MESSAGES = {
  customer_profile_label_too_long: 'setup.customerProfile.refusals.labelTooLong',
  customer_profile_label_required: 'setup.customerProfile.refusals.labelRequired',
  customer_profile_tried_too_many: 'setup.customerProfile.refusals.triedTooMany',
  customer_profile_title_too_long: 'setup.customerProfile.refusals.titleTooLong',
  customer_profile_summary_too_long: 'setup.customerProfile.refusals.summaryTooLong',
  customer_profile_rows_empty: 'setup.customerProfile.refusals.rowsEmpty',
  customer_profile_run_in_progress: 'setup.customerProfile.refusals.runInProgress',
  customer_profile_facts_missing: 'setup.customerProfile.refusals.factsMissing',
  customer_profile_competitors_missing: 'setup.customerProfile.refusals.competitorsMissing',
  owner_admin_role_required: 'setup.customerProfile.refusals.ownerAdminRequired',
  editor_role_required: 'setup.customerProfile.refusals.editorRequired',
} as const satisfies Record<CustomerProfileRefusalReason, string>

/** The line under the reading ring for each draft stage. */
export const CUSTOMER_PROFILE_STAGE_LINES = {
  draft: 'setup.customerProfile.reading.detail',
} as const satisfies Record<CustomerProfileRunStage, string> & SetupRunStageLines

/** The i18n keys of each row: its title, the hint under it in the edit dialog, and its edit title. */
export const PROFILE_CARD_MESSAGES = {
  tried: {
    title: 'setup.customerProfile.cards.tried.title',
    hint: 'setup.customerProfile.cards.tried.hint',
    editTitle: 'setup.customerProfile.cards.tried.editTitle',
  },
  blockers: {
    title: 'setup.customerProfile.cards.blockers.title',
    hint: 'setup.customerProfile.cards.blockers.hint',
    editTitle: 'setup.customerProfile.cards.blockers.editTitle',
  },
} as const satisfies Record<
  ProfileRowId,
  { readonly title: string; readonly hint: string; readonly editTitle: string }
>

export const PROFILE_CARD_ICONS = {
  tried: RefreshIcon,
  blockers: ShieldIcon,
} as const satisfies Record<ProfileRowId, Component>

/** The i18n key of each company size band, as the size select lists it. */
export const COMPANY_SIZE_LABELS = {
  solo: 'setup.customerProfile.companySize.solo',
  '2-10': 'setup.customerProfile.companySize.upTo10',
  '11-50': 'setup.customerProfile.companySize.upTo50',
  '51-200': 'setup.customerProfile.companySize.upTo200',
  '201-1000': 'setup.customerProfile.companySize.upTo1000',
  '1000+': 'setup.customerProfile.companySize.over1000',
} as const satisfies Record<CompanySizeBand, string>
